/**
 * «Обзор СПП» (AK-740) — прошлое / текущее / варианты будущего, drill-down
 * компания → кластер → хозяйство.
 *
 * Спека (утв. founder 23.08): `docs/superpowers/specs/2026-08-23-spp-overview-
 * requirements.md`. Wireframes (APPROVED):
 * `docs/superpowers/prototypes/2026-08-23-spp-overview/{01-company,02-cluster,
 * 03-farm}.html`.
 *
 * ГЛАВНАЯ метрика — % (решение founder 23.08): в ячейке крупный текст — %,
 * гектары — мелкая вторая строка. Слово «пашня» в интерфейсе не пишем.
 *
 * Уровень адресуется query-параметрами `level`/`id` (не путём вида
 * `/cluster/yug`, как в wireframe-мокапе): один роут на три уровня экрана
 * проще держать в согласии с `GET /planning/spp-overview?level=&id=`, и это
 * тот же приём, что уже несут соседи по разделу (`?farm=`, `?variant=` у
 * СПП-варианта). Без `id` компания резолвится автоматически — как в хабе
 * `/planning` (`getSeasonConfig` уже это умеет).
 *
 * `?variants=` — до 3 UUID через запятую, ЯВНЫЙ выбор сравниваемых вариантов
 * будущего на уровне хозяйства (ревью head-web #892 п.20): URL — единственный
 * источник правды, отдельного React-стейта нет. Без параметра сервер сам
 * решает (основной/первый вариант, `is_shown` в ответе); параметр появляется
 * только когда пользователь тронул чип — ссылку «сравнение 1+3» можно кинуть
 * коллеге.
 *
 * ★ «сделать основным» ЖИВЁТ НА ЭТОМ ЭКРАНЕ, хотя спека §Состав п.3 и
 * `03-farm.html` называют местом отметки «существующий список вариантов
 * хозяйства… не на этом экране» (ревью head-web #892 п.7, founder approved
 * wireframe). Отклонение СОЗНАТЕЛЬНОЕ, а не забытое: обзор СПП — единственный
 * экран, где видно ПОСЛЕДСТВИЕ отметки (сумма кластера/компании) сразу же —
 * `VariantTabs` (общий переключатель 4 других экранов планирования) этого
 * контекста не показывает вовсе, и звёздочка там была бы действием вслепую.
 * Компонент `useSetPrimaryVariant` переиспользует ТУ ЖЕ ручку/право/инвалидацию,
 * что и общий список — перенести в `VariantTabs` можно одной правкой, если
 * founder не согласится оставить здесь. Ждём подтверждения.
 */
import { useCallback, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link, useNavigate, useSearch } from '@tanstack/react-router';
import { ChevronRight, Star } from 'lucide-react';
import { AppShell } from '@/components/layout/AppShell';
import { PageHeader } from '@/components/layout/PageHeader';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { CropChip } from '@/components/ui/crop-chip';
import { PageWithRail } from '@/components/ui/page-with-rail';
import { Skeleton } from '@/components/ui/skeleton';
import { Stack } from '@/components/ui/stack';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { useCan } from '@/lib/auth/usePermissions';
import { getCropColor } from '@/lib/colorMaps/cropColors';
import { pluralRu } from '@/lib/dateFmt';
import { getSeasonConfig } from '@/lib/planning/api';
import { sppOverviewKeys } from '@/lib/planning/queryKeys';
import {
  formatSppHa,
  formatSppPct,
  formatSppYield,
  getSppOverview,
  isServiceOccupation,
  type SppCell,
  type SppChild,
  type SppColumn,
  type SppLevel,
  type SppOverview,
  type SppRow,
} from '@/lib/planning/sppOverview';
import { useSetPrimaryVariant } from '@/lib/planning/techCards';

const MAX_FUTURE_VARIANTS = 3;

interface SppSearch {
  level?: SppLevel;
  id?: string;
  variants?: string;
}

function parseVariantsParam(raw: string | undefined): string[] | null {
  if (!raw) return null;
  const ids = raw.split(',').filter(Boolean);
  return ids.length > 0 ? ids : null;
}

/**
 * Текст цвета сегмента ShareBars — ФИКСИРОВАННЫЙ тёмный, не `text-foreground`
 * (ревью head-web #892 п.12): фон сегмента — пастель из домен-палитры
 * `cropColors` (`#FFF9E6` и т.п.), которая НЕ переключается по теме, а
 * `text-foreground` в dark mode светлый — почти белый текст по бледно-жёлтому
 * нечитаем. Тот же принцип, что у самого `cropColors.ts` (домен-палитра, не
 * UI-тема): раз фон не следует теме, текст поверх него тоже не должен.
 */
const SEGMENT_INK = '#292524';

/** Ячейка таблицы: % крупно (главная метрика), га мелко, урожайность строкой ниже. */
function SppCellView({ cell }: { cell: SppCell | undefined }) {
  if (!cell) {
    return <span className="text-muted-foreground">—</span>;
  }
  return (
    <div className="flex flex-col">
      <span className="text-base font-bold leading-tight text-foreground">
        {formatSppPct(cell.pct)}
      </span>
      <span className="text-xs tabular-nums text-muted-foreground">{formatSppHa(cell.area_ha)}</span>
      <span
        className={
          cell.yield_status === 'growing' || cell.yield_status === 'no_data'
            ? 'text-xs italic text-accent-brand'
            : cell.yield_status === 'estimate'
              ? 'text-xs italic text-muted-foreground'
              : 'text-xs text-muted-foreground'
        }
      >
        {formatSppYield(cell)}
      </span>
    </div>
  );
}

/** Колонка будущего для полос: на хозяйстве — вариант (основной), выше — сумма. */
function futureBarColumn(data: SppOverview): SppColumn | undefined {
  if (data.level === 'farm') {
    return (
      data.columns.find((c) => c.kind === 'future_variant' && c.is_primary) ??
      data.columns.find((c) => c.kind === 'future_variant')
    );
  }
  return data.columns.find((c) => c.kind === 'future_sum');
}

/**
 * Подпись полосы: план помечается СЛОВОМ, а не только положением справа
 * (AK-748). «Будущее-2027» в шапке таблицы читается как год-факт, стоит его
 * вынести в полосы без пометки — и картинка «три года подряд» врёт: два
 * измеренных и один намеренный выглядят одинаково.
 */
function barColumnLabel(col: SppColumn): string {
  if (col.kind === 'fact') return col.label;
  return col.year !== null ? `${col.year} (план)` : `${col.label} (план)`;
}

/**
 * Подпись под заголовком полос — что именно человек видит.
 *
 * План будущего теперь ВХОДИТ в полосы (AK-748, требование founder: «когда тут
 * 2027 появится»). Полоса плана короче фактовых — и это ЧЕСТНО: знаменатель у
 * всех колонок один (сегодняшний фонд полей), а гектары в плановой колонке
 * дают только те хозяйства, чей вариант РАЗЛОЖЕН ПО ПОЛЯМ. Растягивать полосу
 * до края нельзя, поэтому подпись сама называет причину: иначе короткая полоса
 * читается как поломка, а не как «часть фонда не запланирована».
 *
 * Цифра в подписи — `farms_with_plan`, а НЕ `farms_with_scenario` (ревью
 * head-web Б1 по `a8cd3d18`). Это разные множества, и бэк держит
 * `farms_with_scenario >= farms_with_plan` по построению: длину полосы задаёт
 * второе (у кого есть строки), а первое («у кого заведён вариант») отправило бы
 * искать недостающие гектары в хозяйстве, которое их и не давало. Хуже того,
 * условие показа по `farms_with_scenario` МОЛЧАЛО в главном кейсе: варианты
 * заведены у всех, разложены у трёх — полоса короткая, объяснения нет.
 *
 * `years` берёт ТОЛЬКО `kind === 'fact'` (ревью head-web по AK-746 предупреждал
 * ровно об этом): без фильтра колонка будущего попала бы в перечисление под
 * словом «факт» — подпись назвала бы план фактом.
 *
 * Часть без фактовых колонок — не гипотеза (ревью head-web Б2): новый тенант
 * заводит план-2027, не имея ни одного прошлого сезона. Собираем подпись из
 * кусков, а не склейкой «факт  · план» с дырой на месте годов.
 */
function barsCaption(data: SppOverview, barColumns: SppColumn[]): string {
  if (data.level === 'farm') return 'текущий сезон vs основной вариант будущего';
  const years = barColumns.filter((c) => c.kind === 'fact').map((c) => c.label);
  const future = barColumns.find((c) => c.kind !== 'fact');
  const parts: string[] = [];
  if (years.length > 0) parts.push(`факт ${years.join(' и ')}`);
  if (future) {
    const covered = future.farms_with_plan;
    const total = future.farms_total;
    const partial =
      covered !== null && total !== null && covered < total
        ? ` — план разложен по полям у ${covered} из ${total} ${pluralRu(total, [
            'хозяйства',
            'хозяйств',
            'хозяйств',
          ])}, поэтому полоса короче`
        : '';
    parts.push(`план «${future.label}»${partial}`);
  }
  return parts.join(' · ');
}

/**
 * Полосы долей: company/cluster — 2 факт-года + план будущего (AK-748); farm —
 * ТЕКУЩИЙ сезон vs ОСНОВНОЙ вариант будущего (03-farm.html, ревью head-web
 * #892 п.6 — раньше на всех трёх уровнях брались два факт-года, и на
 * хозяйстве сравнения «сегодня vs будущее» не было вовсе).
 */
function ShareBars({ data }: { data: SppOverview }) {
  const factColumns = data.columns.filter((c) => c.kind === 'fact');
  const future = futureBarColumn(data);
  const barColumns: SppColumn[] = (
    data.level === 'farm'
      ? [factColumns.find((c) => c.is_current) ?? factColumns[factColumns.length - 1], future]
      : [...factColumns.slice(-2), future]
  ).filter(Boolean) as SppColumn[];
  if (barColumns.length === 0) return null;
  // Строки без площади НИ В ОДНОЙ показанной полосе выкидываем целиком
  // (AK-748): в полосе такой строки нет по определению (сегмент рисуется от
  // pct > 0), а в легенде она занимала место — легенда описывала не картинку,
  // а весь ответ ручки. Служебные занятия с РЕАЛЬНОЙ площадью («Передано в
  // аренду», «Пастбище») этим не отсекаются — им нужен признак в данных,
  // см. комментарий к легенде ниже.
  const shownKeys = barColumns.map((c) => c.key);
  const cropRows = data.rows.filter(
    (r) =>
      (r.kind === 'crop' || r.kind === 'fallow') &&
      // Служебные занятия («Передано в аренду», «Пастбище», «Раскорчевка») —
      // не культуры и не доля структуры посевов. Признак приходит из разбора
      // тенант-каталога, а не угадывается по названию.
      !isServiceOccupation(r) &&
      shownKeys.some((key) => (r.cells[key]?.area_ha ?? 0) > 0),
  );
  return (
    <Card>
      <CardHeader className="p-4 pb-3">
        <CardTitle className="text-base">Доля культур</CardTitle>
        <p className="text-xs text-muted-foreground">{barsCaption(data, barColumns)}</p>
      </CardHeader>
      <CardContent className="p-4 pt-0">
        <Stack gap={3}>
          {barColumns.map((col) => (
            <div key={col.key} className="flex items-center gap-3">
              <span className="w-16 shrink-0 text-xs font-medium text-muted-foreground">
                {barColumnLabel(col)}
              </span>
              <div className="flex h-7 flex-1 overflow-hidden rounded-md border border-border">
                {cropRows.map((row) => {
                  const cell = row.cells[col.key];
                  if (!cell || cell.pct === null || cell.pct <= 0) return null;
                  const color = getCropColor(row.name);
                  return (
                    <div
                      key={row.key}
                      title={`${row.name} — ${formatSppPct(cell.pct)}`}
                      style={{ width: `${cell.pct}%`, backgroundColor: color.bg, color: SEGMENT_INK }}
                      className="flex items-center justify-center overflow-hidden whitespace-nowrap text-[11px] font-semibold"
                    >
                      {cell.pct >= 8 ? formatSppPct(cell.pct) : ''}
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
          {/* Легенда = ровно то, что нарисовано в полосах: служебные занятия
              отсеяны там же, где и сегменты (`cropRows`), одним признаком. */}
          <div className="flex flex-wrap gap-3 border-t border-border pt-3">
            {cropRows.map((row) => (
              <span key={row.key} className="flex items-center gap-1 text-xs text-muted-foreground">
                <span
                  className="h-2.5 w-2.5 rounded-sm border border-border"
                  style={{ backgroundColor: getCropColor(row.name).bg }}
                />
                <CropChip name={row.name} iconSize={12} />
              </span>
            ))}
          </div>
        </Stack>
      </CardContent>
    </Card>
  );
}

/** Строка таблицы. `muted` — служебное занятие (не культура), приглушено. */
function SppTableRow({
  row,
  columns,
  muted = false,
}: {
  row: SppRow;
  columns: SppColumn[];
  muted?: boolean;
}) {
  return (
    <TableRow className={muted ? 'text-muted-foreground' : undefined}>
      <TableCell className="font-medium">
        <span className="flex items-center gap-2">
          <span
            className="h-2.5 w-2.5 shrink-0 rounded-sm border border-border"
            // У служебной строки квадратик пустой: цвет из палитры культур —
            // это ключ к полосам, а служебного занятия в полосах нет.
            style={muted ? undefined : { backgroundColor: getCropColor(row.name).bg }}
          />
          <CropChip name={row.name} />
        </span>
      </TableCell>
      {columns.map((col) => (
        <TableCell key={col.key}>
          <SppCellView cell={row.cells[col.key]} />
        </TableCell>
      ))}
    </TableRow>
  );
}

/** Селектор вариантов будущего (только уровень хозяйства) — до 3, ★ = основной. */
function VariantChips({
  data,
  selected,
  onToggle,
}: {
  data: SppOverview;
  selected: string[];
  onToggle: (id: string) => void;
}) {
  // Право `season_plan:U` — то же, чем заводится/переименовывается вариант
  // (`CreateVariantButton`/`RenameVariantButton`): без него звёздочку не
  // показываем вовсе (fail-closed, гейт ДО входа во флоу, не 403 на клик).
  const canEditPrimary = useCan('season_plan', 'u');
  const setPrimary = useSetPrimaryVariant();

  if (data.variants.length === 0) {
    // Пустое состояние (ревью head-web #892 п.9) — экран не молчит: у
    // хозяйства нет ни одного варианта плана на будущее, суммировать/сравнивать
    // нечего. Ссылка ведёт в хаб планирования — точный год варианта здесь
    // неизвестен без единой колонки будущего, поэтому не гадаем адрес.
    return (
      <Card>
        <CardContent className="p-4">
          <p className="text-sm text-muted-foreground">
            У хозяйства пока нет ни одного варианта плана на будущий сезон — сравнивать и
            суммировать нечего.
          </p>
          <Button asChild size="sm" variant="outline" className="mt-2">
            <Link to="/planning">Открыть планирование сезона</Link>
          </Button>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader className="p-4 pb-3">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <CardTitle className="text-base">Варианты будущего</CardTitle>
          <span className="text-xs text-muted-foreground">
            выбрано {selected.length} из {data.variants.length} · сравнивать можно до{' '}
            {MAX_FUTURE_VARIANTS}
          </span>
        </div>
      </CardHeader>
      <CardContent className="p-4 pt-0">
        <div className="flex flex-wrap gap-2">
          {data.variants.map((v) => {
            const isOn = selected.includes(v.scenario_id);
            const atMax = !isOn && selected.length >= MAX_FUTURE_VARIANTS;
            const isLastOn = isOn && selected.length === 1;
            const chipDisabled = atMax || isLastOn;
            return (
              <div key={v.scenario_id} className="flex items-center gap-1">
                <Button
                  size="sm"
                  variant={isOn ? 'secondary' : 'outline'}
                  onClick={() => onToggle(v.scenario_id)}
                  aria-pressed={isOn}
                  disabled={chipDisabled}
                  title={
                    atMax
                      ? `Сравнивать можно не больше ${MAX_FUTURE_VARIANTS} вариантов — снимите другой`
                      : isLastOn
                        ? 'Должна остаться хотя бы одна колонка будущего'
                        : undefined
                  }
                >
                  {v.is_primary && <Star className="mr-1 h-3.5 w-3.5 fill-current text-accent-brand" />}
                  {v.name}
                </Button>
                {!v.is_primary && canEditPrimary && (
                  <Button
                    size="sm"
                    variant="ghost"
                    aria-label={`Сделать «${v.name}» основным`}
                    title="Сделать основным — будет суммироваться в обзоре кластера/компании"
                    disabled={setPrimary.isPending}
                    onClick={() => setPrimary.mutate({ scenarioId: v.scenario_id })}
                  >
                    <Star className="h-3.5 w-3.5" />
                  </Button>
                )}
              </div>
            );
          })}
        </div>
        <p className="mt-2 text-xs text-muted-foreground">
          ★ — основной вариант, суммируется в обзоре кластера и компании. Отметка
          переиспользует существующий список вариантов хозяйства.
        </p>
      </CardContent>
    </Card>
  );
}

function subLabelText(child: SppChild): string {
  // `switch` с явной третьей веткой (ревью head-web #892 п.18) — тернарник
  // раньше подписывал ЛЮБОЕ не-`farms` значение «вариантов будущего», и
  // будущий `sub_label='clusters'` (контракт его уже допускает) получил бы
  // неверный текст молча.
  switch (child.sub_label) {
    case 'farms':
      return `${child.sub_count} ${pluralRu(child.sub_count, ['хозяйство', 'хозяйства', 'хозяйств'])}`;
    case 'clusters':
      return `${child.sub_count} ${pluralRu(child.sub_count, ['кластер', 'кластера', 'кластеров'])}`;
    case 'variants':
      return `${child.sub_count} ${pluralRu(child.sub_count, ['вариант', 'варианта', 'вариантов'])} будущего`;
  }
}

function ChildrenSidebar({ data, level }: { data: SppOverview; level: SppLevel }) {
  if (level === 'farm' || data.children.length === 0) return null;
  const title = level === 'company' ? 'Кластеры' : 'Хозяйства кластера';
  return (
    <Card>
      <CardHeader className="p-3 pb-2">
        <CardTitle className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
          {title}
        </CardTitle>
      </CardHeader>
      <CardContent className="p-0">
        <ul>
          {data.children.map((child) => (
            <li key={child.id} className="border-t border-border first:border-t-0">
              <Link
                to="/planning/spp-overview"
                // Хозяйство БЕЗ кластера ведёт СРАЗУ на farm, а не на
                // несуществующий уровень «кластер» (backend ревью #891 п.10:
                // раньше nextLevel вычислялся ТОЛЬКО из level родителя —
                // такое хозяйство входит в агрегат, но до него было не дойти).
                search={{ level: child.level, id: child.id }}
                className="flex items-center gap-3 p-3 text-sm hover:bg-accent/40"
              >
                <span className="min-w-0 flex-1">
                  <span className="block font-medium text-foreground">{child.name}</span>
                  <span className="block text-xs text-muted-foreground">
                    {formatSppHa(child.arable_ha)} · {subLabelText(child)}
                  </span>
                </span>
                <ChevronRight className="h-4 w-4 shrink-0 text-muted-foreground" />
              </Link>
            </li>
          ))}
        </ul>
      </CardContent>
    </Card>
  );
}

function Breadcrumbs({ data }: { data: SppOverview }) {
  const crumbs: { label: string; level: SppLevel; id: string; current: boolean }[] = [
    { label: data.company_name, level: 'company', id: data.company_id, current: data.level === 'company' },
  ];
  if (data.cluster_id && data.cluster_name) {
    crumbs.push({
      label: data.cluster_name,
      level: 'cluster',
      id: data.cluster_id,
      current: data.level === 'cluster',
    });
  }
  if (data.level === 'farm') {
    crumbs.push({ label: data.name, level: 'farm', id: data.id, current: true });
  }
  return (
    <div className="flex flex-wrap items-center gap-1 text-sm text-muted-foreground">
      {crumbs.map((c, i) => (
        <span key={`${c.level}-${c.id}`} className="flex items-center gap-1">
          {i > 0 && <span aria-hidden>›</span>}
          {c.current ? (
            <span className="font-semibold text-foreground">{c.label}</span>
          ) : (
            <Link
              to="/planning/spp-overview"
              search={{ level: c.level, id: c.id }}
              className="text-primary hover:underline"
            >
              {c.label}
            </Link>
          )}
        </span>
      ))}
    </div>
  );
}

/**
 * График долей + таблица — общая основная колонка для всех трёх уровней.
 *
 * Порядок блоков: «Доля культур» ПЕРВОЙ, таблица под ней (AK-746, решение
 * founder 25.08). Полосы долей читаются с одного взгляда и отвечают на
 * главный вопрос экрана — «что у нас в структуре»; таблица с процентами,
 * гектарами и урожайностью по каждой колонке — это уже детализация, за
 * которой спускаются осознанно.
 */
function MainColumn({ data }: { data: SppOverview }) {
  // Служебные занятия — отдельной секцией внизу таблицы (комментарий у самой
  // секции), а из «Доли культур» они уходят совсем.
  const cropRows = data.rows.filter((r) => !isServiceOccupation(r));
  const serviceRows = data.rows.filter((r) => isServiceOccupation(r));
  return (
    <Stack gap={4}>
      <ShareBars data={data} />

      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Культура</TableHead>
                {data.columns.map((col) => (
                  <TableHead key={col.key}>
                    {col.label}
                    {col.is_current && (
                      <span className="ml-1 text-[10px] font-medium text-accent-brand">текущий</span>
                    )}
                    {col.kind !== 'fact' && col.is_primary && (
                      <span className="ml-1 text-[10px] font-medium text-accent-brand">★ основной</span>
                    )}
                    {col.kind === 'future_sum' && col.farms_total !== null && (
                      <span className="block text-[10px] font-normal normal-case tracking-normal text-muted-foreground">
                        вариант у {col.farms_with_scenario} из {col.farms_total}{' '}
                        {pluralRu(col.farms_total ?? 0, ['хозяйства', 'хозяйств', 'хозяйств'])}, строки есть у{' '}
                        {col.farms_with_plan}
                      </span>
                    )}
                    {/* Пустой вариант ≠ вариант без строк (ревью head-web
                        #892 п.10, backend ревью #891 блокер п.2): бэк честно
                        отдаёт `rows_from_fields`, экран обязан его показать,
                        а не рисовать прочерки неотличимо от «данных нет». */}
                    {col.kind !== 'fact' && !col.rows_from_fields && (
                      <span className="block text-[10px] font-normal normal-case tracking-normal text-muted-foreground">
                        строк пока нет
                      </span>
                    )}
                  </TableHead>
                ))}
              </TableRow>
            </TableHeader>
            <TableBody>
              {cropRows.map((row) => (
                <SppTableRow key={row.key} row={row} columns={data.columns} />
              ))}
              {/* Служебные занятия ОСТАЮТСЯ в таблице — «Передано в аренду» и
                  «Пастбище» founder'у информативны, из фонда они никуда не
                  делись. Но не как культура: отдельной секцией внизу и
                  приглушённо, а в долях структуры посевов их нет вовсе. */}
              {serviceRows.length > 0 && (
                <TableRow className="border-t-2 border-border bg-muted/20 hover:bg-muted/20">
                  <TableCell
                    colSpan={data.columns.length + 1}
                    className="py-2 text-xs font-medium text-muted-foreground"
                  >
                    Не культуры — площадь учтена в фонде, в доле культур не участвует
                  </TableCell>
                </TableRow>
              )}
              {serviceRows.map((row) => (
                <SppTableRow key={row.key} row={row} columns={data.columns} muted />
              ))}
              {data.rows.length === 0 && (
                <TableRow>
                  <TableCell colSpan={data.columns.length + 1} className="text-sm text-muted-foreground">
                    Посевов в выбранном периоде нет.
                  </TableCell>
                </TableRow>
              )}
              <TableRow className="border-t-2 border-border bg-muted/30 font-semibold">
                <TableCell>Итого</TableCell>
                {data.columns.map((col) => (
                  <TableCell key={col.key}>
                    <SppCellView cell={data.totals[col.key]} />
                  </TableCell>
                ))}
              </TableRow>
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <p className="text-xs text-muted-foreground">
        «Итого» может быть и меньше 100% (поля списывались или добавлялись позже), и больше
        100% (пересев на той же площади в одном сезоне).
      </p>
    </Stack>
  );
}

export function SppOverviewPage() {
  const search = useSearch({ strict: false }) as SppSearch;
  const navigate = useNavigate();

  // Без явного уровня/id — резолвим компанию так же, как хаб `/planning`.
  const config = useQuery({
    queryKey: ['planning', 'season-config', null],
    queryFn: () => getSeasonConfig(null),
    enabled: !search.id,
    staleTime: 60_000,
  });
  const configCompanyId = config.data?.company_id ?? null;
  const configFailed = !search.id && (config.isError || (config.data != null && !configCompanyId));

  const goTo = useCallback(
    (next: SppSearch) => {
      void navigate({ to: '/planning/spp-overview', search: next, replace: true });
    },
    [navigate],
  );

  useEffect(() => {
    if (!search.id && configCompanyId) {
      goTo({ level: 'company', id: configCompanyId });
    }
  }, [search.id, configCompanyId, goTo]);

  const level: SppLevel = search.level ?? 'company';
  const id = search.id;

  // Явный выбор вариантов будущего — В URL, не в React-стейте (ревью head-web
  // #892 п.20): без параметра сервер сам решает (основной/первый, `is_shown`
  // в ответе), параметр появляется только когда пользователь тронул чип.
  const explicitVariants = level === 'farm' ? parseVariantsParam(search.variants) : null;

  const overview = useQuery({
    queryKey: sppOverviewKeys.overview(level, id ?? null, explicitVariants ?? []),
    queryFn: () =>
      getSppOverview({
        level,
        id: id as string,
        variantIds: explicitVariants ?? undefined,
      }),
    enabled: Boolean(id),
    staleTime: 30_000,
  });

  // Что показано СЕЙЧАС на чипах: явный выбор пользователя (URL), а до него —
  // `is_shown` из ответа (сервер уже посчитал дефолтные колонки сам).
  const displayedVariants =
    explicitVariants ?? overview.data?.variants.filter((v) => v.is_shown).map((v) => v.scenario_id) ?? [];

  const toggleVariant = (scenarioId: string) => {
    const base = displayedVariants;
    let next: string[];
    if (base.includes(scenarioId)) {
      if (base.length === 1) return; // нельзя остаться без единственной колонки будущего
      next = base.filter((v) => v !== scenarioId);
    } else {
      if (base.length >= MAX_FUTURE_VARIANTS) return;
      next = [...base, scenarioId];
    }
    goTo({ level, id, variants: next.length > 0 ? next.join(',') : undefined });
  };

  const data = overview.data;

  return (
    <AppShell>
      <div className="mx-auto w-full max-w-[1200px] pb-safe-fab">
        <PageHeader
          title="Обзор СПП"
          showBack
          breadcrumbs={[{ label: 'Планирование сезона', to: '/planning' }, { label: 'Обзор СПП' }]}
          description="Структура посевных площадей: как менялась по годам и что запланировано на будущее. Drill-down: вся компания → кластер → хозяйство."
        />

        <Stack gap={4} className="mt-4">
          {!search.id && config.isLoading && <Skeleton className="h-24 w-full" />}

          {/* Компания не резолвилась (ревью head-web #892 п.11) — раньше
              `config.isError`/`company_id == null` не обрабатывались вовсе:
              `id` не появлялся, `overview` оставался `disabled`, скелет
              снимался — белый экран без единого слова. */}
          {configFailed && (
            <Card>
              <CardContent className="p-4 text-sm text-destructive">
                Не удалось определить компанию для обзора СПП. Обновите страницу или
                откройте раздел «Планирование сезона» заново.
              </CardContent>
            </Card>
          )}

          {search.id && overview.isLoading && <Skeleton className="h-96 w-full" />}
          {search.id && overview.isError && (
            <Card>
              <CardContent className="p-4 text-sm text-destructive">
                Не удалось загрузить обзор СПП. Обновите страницу.
              </CardContent>
            </Card>
          )}

          {data && (
            <>
              <Breadcrumbs data={data} />

              <div className="flex flex-wrap items-center gap-2 text-sm">
                <Badge variant="outline">Фонд полей: {formatSppHa(data.arable_ha)}</Badge>
                {data.level !== 'farm' && (
                  <Badge variant="outline">
                    {data.farms_count} {pluralRu(data.farms_count, ['хозяйство', 'хозяйства', 'хозяйств'])}
                  </Badge>
                )}
                <Badge variant="outline">
                  {data.crops_count} {pluralRu(data.crops_count, ['культура', 'культуры', 'культур'])} в обороте
                </Badge>
              </div>

              {/* Честная подпись про базу % (backend ревью #891, ответ (а);
                  двусторонняя формулировка — ревью head-web #892 п.21):
                  знаменатель ОДИН на все колонки — сегодняшний фонд полей, а
                  не площадь каждого года отдельно. «Итого» может быть и МЕНЬШЕ
                  100% (поля заводились позже/списывались), и БОЛЬШЕ (пересев
                  в одном сезоне на той же площади) — без подписи любое из
                  этих чисел читалось бы как ошибка, а не как база расчёта. */}
              {/* Дисклеймер разрезан по предложениям (ревью head-web по
                  AK-746): про базу расчёта — здесь, оно работает и на график, и
                  на таблицу; про «Итого» больше/меньше 100% — вниз, под саму
                  таблицу, где эта строка и стоит. Двумя абзацами серого над
                  экраном-героем оно отжимало график, ради которого экран
                  открывают. */}
              <p className="text-xs text-muted-foreground">
                Структура посчитана по сегодняшнему фонду полей — доли по годам сравнимы между
                собой напрямую.
              </p>

              {data.level === 'farm' && (
                <VariantChips data={data} selected={displayedVariants} onToggle={toggleVariant} />
              )}

              {data.level === 'farm' ? (
                // Rail НЕ рендерим на farm вовсе (ревью head-web #892 п.2) —
                // `ChildrenSidebar` там и так возвращает `null`, но
                // `PageWithRail` держит вторую колонку грида структурно:
                // раньше это давало 260px мёртвого поля справа от таблицы.
                <MainColumn data={data} />
              ) : (
                <PageWithRail rail={<ChildrenSidebar data={data} level={level} />}>
                  <MainColumn data={data} />
                </PageWithRail>
              )}
            </>
          )}
        </Stack>
      </div>
    </AppShell>
  );
}

export default SppOverviewPage;
